import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";

export default function EduReportPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [model, setModel] = useState(null);
  const [err, setErr] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get(`/edu/reports/${id}`).then((r) => setModel(r.data))
      .catch((ex) => setErr(formatApiError(ex)));
  }, [id, user]);

  const doExport = async () => {
    setExporting(true); setErr(null);
    try {
      const ex = await api.post(`/edu/reports/${id}/export`);
      const pdf = await api.get(`/edu/exports/${ex.data.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(pdf.data);
      const a = document.createElement("a");
      a.href = url; a.download = `plangmina-edu-${ex.data.id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (ex2) { setErr(formatApiError(ex2)); }
    finally { setExporting(false); }
  };

  if (err && !model) return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center" data-testid="edu-report-error">
      <p className="text-sm text-red-600">{err}</p>
      <Link to="/edu/panel" className="mt-4 inline-block text-sm underline">Wróć do panelu EDU</Link>
    </div>);
  if (!model) return <div className="p-20 text-center text-slate-500" data-testid="edu-report-loading">Ładowanie…</div>;

  const ac = model.academic || {};
  const findings = model.findings || {};

  return (
    <div className="mx-auto max-w-3xl px-4 py-8" data-testid="edu-report-page">
      <Helmet><title>{`Raport akademicki ${model.meta?.ref_id || ""} — PlanGmina EDU`}</title></Helmet>

      <div className="rounded-xl border-2 border-red-700 bg-red-50 p-4" data-testid="edu-report-banner">
        <p className="text-sm font-bold uppercase tracking-wide text-red-800">{ac.watermark_pl}</p>
        <p className="mt-1 text-xs text-red-700">{ac.watermark_en}</p>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Raport akademicki — działka {model.identity?.number}</h1>
          <p className="text-xs text-slate-500">{model.meta?.ref_id} · wygenerowano {model.meta?.generated_at?.slice(0, 16).replace("T", " ")} UTC</p>
        </div>
        <button onClick={doExport} disabled={exporting} data-testid="edu-export-button"
          className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-50">
          {exporting ? "Generowanie PDF…" : "Eksportuj PDF (ze znakiem wodnym)"}
        </button>
      </div>
      {err && <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700" data-testid="edu-export-error">{err}</p>}

      <section className="mt-6 rounded-xl border border-slate-300 p-4" data-testid="edu-report-identity">
        <h2 className="text-sm font-bold uppercase text-slate-500">Kontekst akademicki</h2>
        <div className="overflow-x-auto">
        <table className="mt-2 w-full text-sm">
          <tbody>
            <tr><td className="py-1 pr-3 text-slate-500">Uczelnia</td><td>{ac.institution_name}</td></tr>
            <tr><td className="py-1 pr-3 text-slate-500">Projekt</td><td>{ac.project_name} {ac.department ? `· ${ac.department}` : ""}</td></tr>
            <tr><td className="py-1 pr-3 text-slate-500">Rola autora</td><td>{ac.user_role}</td></tr>
            <tr><td className="py-1 pr-3 text-slate-500">Licencja</td><td>EDU — użytek niekomercyjny ({ac.terms_version})</td></tr>
          </tbody>
        </table>
        </div>
      </section>

      <section className="mt-6" data-testid="edu-report-executive">
        <h2 className="text-sm font-bold uppercase text-slate-500">Podsumowanie wykonawcze</h2>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-700">
          {(model.executive || []).map((l, i) => <li key={i}>{l}</li>)}
        </ol>
      </section>

      <section className="mt-6" data-testid="edu-report-findings">
        <h2 className="text-sm font-bold uppercase text-slate-500">Warstwy dowodowe</h2>
        {Object.entries(findings).map(([k, f]) => (
          <div key={k} className="mt-3 rounded-xl border border-slate-200 p-4" data-testid={`edu-finding-${k}`}>
            <p className="text-sm font-semibold">{f.title_pl}</p>
            <p className={`mt-1 inline-block rounded border px-2 py-0.5 text-xs font-bold uppercase ${f.unavailable ? "border-dashed border-slate-400 text-slate-500" : "border-slate-900"}`}>
              {f.badge_pl || f.status}
            </p>
            {f.note_pl && <p className="mt-2 text-xs text-slate-600">{f.note_pl}</p>}
            {f.provenance?.authority && (
              <p className="mt-2 border-t border-slate-100 pt-1 text-[11px] text-slate-400">
                {f.provenance.authority} · pobrano {(f.provenance.retrieved_at || "").slice(0, 19)}
              </p>)}
          </div>))}
      </section>

      <section className="mt-6 rounded-xl border border-slate-300 p-4" data-testid="edu-report-provenance">
        <h2 className="text-sm font-bold uppercase text-slate-500">Proweniencja źródeł</h2>
        <div className="overflow-x-auto">
        <table className="mt-2 w-full text-left text-xs">
          <thead><tr className="border-b border-slate-200 text-slate-500">
            <th className="py-1 pr-2">Warstwa</th><th className="pr-2">Organ</th><th>Pobrano (UTC)</th>
          </tr></thead>
          <tbody>
            {(model.provenance || []).map((p) => (
              <tr key={p.key} className="border-b border-slate-100">
                <td className="py-1 pr-2">{p.title_pl}</td><td className="pr-2">{p.authority}</td>
                <td>{(p.retrieved_at || "").slice(0, 19)}</td>
              </tr>))}
          </tbody>
        </table>
        </div>
      </section>

      {model.citation?.suggested_pl && (
        <section className="mt-6 rounded-xl border border-slate-900 p-4 text-sm" data-testid="edu-report-citation">
          <h2 className="text-sm font-bold uppercase text-slate-500">Jak cytować</h2>
          <p className="mt-2 text-slate-700">{model.citation.suggested_pl}</p>
        </section>)}

      <p className="mt-6 border-t border-slate-200 pt-3 text-xs text-slate-500">
        SHA-256 treści: <code className="break-all">{model.integrity?.report_hash}</code> · {ac.license_notice_pl}
      </p>
    </div>
  );
}
